import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BellRing, Plus, Trash2 } from 'lucide-react'
import { api, messageErreur } from '../lib/api'
import { Carte, Champ, Chargement, EtatVide, Message, Pastille } from './base'
import { SEVERITES } from '@doc/shared'

type Parametre = {
  code: string
  libelle: string
  categorie: string
  uniteCible: string
  unitesReconnues: string[]
  conversions: Record<string, number>
  conversionsIncertaines: string[]
  note: string | null
}

type Regle = {
  id: string
  parametreCode: string | null
  parametreLibelle: string
  operateur: 'SUPERIEUR' | 'SUPERIEUR_OU_EGAL' | 'INFERIEUR' | 'INFERIEUR_OU_EGAL' | 'HORS_REFERENCES'
  seuil: number | null
  unite: string | null
  severite: 'INFO' | 'ATTENTION' | 'CRITIQUE'
  message: string | null
  actif: boolean
  _count?: { alertes: number }
}

const OPERATEURS = {
  SUPERIEUR: 'est supérieur à',
  SUPERIEUR_OU_EGAL: 'est supérieur ou égal à',
  INFERIEUR: 'est inférieur à',
  INFERIEUR_OU_EGAL: 'est inférieur ou égal à',
  HORS_REFERENCES: 'sort des références du laboratoire',
} as const

export default function SeuilsAlerte() {
  const client = useQueryClient()
  const [ajout, setAjout] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['regles-alerte'],
    queryFn: () => api<{ regles: Regle[] }>('/api/regles-alerte'),
  })
  const { data: table } = useQuery({
    queryKey: ['parametres-biologie'],
    queryFn: () => api<{ parametres: Parametre[] }>('/api/biologie/parametres'),
    staleTime: 60 * 60 * 1000,
  })

  const [brouillon, setBrouillon] = useState({
    parametreLibelle: '',
    operateur: 'SUPERIEUR' as Regle['operateur'],
    seuil: '',
    severite: 'ATTENTION' as Regle['severite'],
    message: '',
  })

  const parametre = useMemo(() => {
    if (!table) return null
    const cle = brouillon.parametreLibelle.trim().toLowerCase()
    return table.parametres.find((p) => p.libelle.toLowerCase() === cle) ?? null
  }, [table, brouillon.parametreLibelle])

  const rafraichir = () => client.invalidateQueries({ queryKey: ['regles-alerte'] })

  const creer = useMutation({
    mutationFn: () =>
      api('/api/regles-alerte', {
        methode: 'POST',
        corps: {
          parametreCode: parametre?.code ?? null,
          parametreLibelle: brouillon.parametreLibelle.trim(),
          operateur: brouillon.operateur,
          seuil:
            brouillon.operateur === 'HORS_REFERENCES'
              ? null
              : Number.parseFloat(brouillon.seuil.replace(',', '.')),
          severite: brouillon.severite,
          message: brouillon.message || null,
        },
      }),
    onSuccess: () => {
      setBrouillon({
        parametreLibelle: '',
        operateur: 'SUPERIEUR',
        seuil: '',
        severite: 'ATTENTION',
        message: '',
      })
      setAjout(false)
      rafraichir()
    },
    onError: (e) => setErreur(messageErreur(e)),
  })

  const basculer = useMutation({
    mutationFn: (regle: Regle) =>
      api(`/api/regles-alerte/${regle.id}`, { methode: 'PATCH', corps: { actif: !regle.actif } }),
    onSuccess: rafraichir,
  })

  const supprimer = useMutation({
    mutationFn: (id: string) => api(`/api/regles-alerte/${id}`, { methode: 'DELETE' }),
    onSuccess: rafraichir,
  })

  const seuilNecessaire = brouillon.operateur !== 'HORS_REFERENCES'

  return (
    <Carte
      titre="Mes seuils d’alerte"
      icone={<BellRing className="h-[17px] w-[17px] text-bleu-500" />}
      compte={data?.regles.length}
      actions={
        <button className="bouton-discret px-2" onClick={() => setAjout(true)} title="Ajouter un seuil">
          <Plus className="h-4 w-4" />
        </button>
      }
    >
      <div className="px-5 pb-5">
        <p className="mb-3 text-[13px] text-brume">
          Tes seuils, distincts des références des laboratoires. Une alerte n’est levée que sur un
          résultat validé, et apparaît sur l’accueil jusqu’à ce que tu la marques comme traitée.
        </p>

        {isLoading ? (
          <Chargement />
        ) : !data || data.regles.length === 0 ? (
          <EtatVide texte="Aucun seuil défini." />
        ) : (
          <ul className="divide-y divide-trait">
            {data.regles.map((regle) => (
              <li key={regle.id} className="flex items-center gap-3 py-2.5">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--color-bleu-600)]"
                  checked={regle.actif}
                  onChange={() => basculer.mutate(regle)}
                  title={regle.actif ? 'Règle active' : 'Règle en sommeil'}
                />
                <span className={`min-w-0 flex-1 text-[13.5px] ${regle.actif ? '' : 'text-brume'}`}>
                  <span className="font-medium">{regle.parametreLibelle}</span>{' '}
                  {OPERATEURS[regle.operateur]}
                  {regle.seuil !== null ? ` ${regle.seuil} ${regle.unite ?? ''}` : ''}
                  {regle.message && (
                    <span className="block text-[12px] text-brume">{regle.message}</span>
                  )}
                  {(regle._count?.alertes ?? 0) > 0 && (
                    <span className="block text-[12px] text-brume">
                      {regle._count!.alertes} alerte{regle._count!.alertes > 1 ? 's' : ''} levée
                      {regle._count!.alertes > 1 ? 's' : ''} jusqu’ici
                    </span>
                  )}
                </span>
                <Pastille
                  ton={
                    regle.severite === 'CRITIQUE'
                      ? 'critique'
                      : regle.severite === 'ATTENTION'
                        ? 'attention'
                        : 'neutre'
                  }
                >
                  {SEVERITES[regle.severite]}
                </Pastille>
                <button
                  className="bouton-discret px-2 text-critique"
                  onClick={() => {
                    if (window.confirm('Supprimer ce seuil ? Les alertes déjà levées restent.')) {
                      supprimer.mutate(regle.id)
                    }
                  }}
                  title="Supprimer"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {ajout && (
          <form
            className="mt-4 grid grid-cols-1 gap-3 rounded-xl bg-slate-50 p-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault()
              creer.mutate()
            }}
          >
            <Champ
              etiquette="Paramètre"
              className="sm:col-span-2"
              aide={
                parametre
                  ? `Le seuil se compare en ${parametre.uniteCible}, l’unité de présentation.`
                  : 'Choisis un paramètre de la liste pour que le seuil se compare dans la bonne unité.'
              }
            >
              <input
                className="champ"
                list="parametres-seuils"
                required
                value={brouillon.parametreLibelle}
                onChange={(e) =>
                  setBrouillon((b) => ({ ...b, parametreLibelle: e.target.value }))
                }
              />
              <datalist id="parametres-seuils">
                {(table?.parametres ?? []).map((p) => (
                  <option key={p.code} value={p.libelle}>
                    {p.uniteCible}
                  </option>
                ))}
              </datalist>
            </Champ>

            <Champ etiquette="Condition">
              <select
                className="champ"
                value={brouillon.operateur}
                onChange={(e) =>
                  setBrouillon((b) => ({ ...b, operateur: e.target.value as Regle['operateur'] }))
                }
              >
                {Object.entries(OPERATEURS).map(([cle, libelle]) => (
                  <option key={cle} value={cle}>
                    {libelle}
                  </option>
                ))}
              </select>
            </Champ>

            <Champ
              etiquette={seuilNecessaire ? `Seuil${parametre ? ` (${parametre.uniteCible})` : ''}` : 'Seuil'}
            >
              <input
                className="champ"
                required={seuilNecessaire}
                disabled={!seuilNecessaire}
                placeholder={seuilNecessaire ? '100' : 'sans objet'}
                value={brouillon.seuil}
                onChange={(e) => setBrouillon((b) => ({ ...b, seuil: e.target.value }))}
              />
            </Champ>

            <Champ etiquette="Gravité">
              <select
                className="champ"
                value={brouillon.severite}
                onChange={(e) =>
                  setBrouillon((b) => ({ ...b, severite: e.target.value as Regle['severite'] }))
                }
              >
                {Object.entries(SEVERITES).map(([cle, libelle]) => (
                  <option key={cle} value={cle}>
                    {libelle}
                  </option>
                ))}
              </select>
            </Champ>

            <Champ etiquette="Message (facultatif)">
              <input
                className="champ"
                placeholder="Contrôler le bilan martial"
                value={brouillon.message}
                onChange={(e) => setBrouillon((b) => ({ ...b, message: e.target.value }))}
              />
            </Champ>

            {erreur && (
              <div className="sm:col-span-2">
                <Message ton="critique">{erreur}</Message>
              </div>
            )}

            <div className="flex gap-2 sm:col-span-2">
              <button className="bouton-primaire" disabled={creer.isPending}>
                Ajouter ce seuil
              </button>
              <button type="button" className="bouton-discret" onClick={() => setAjout(false)}>
                Annuler
              </button>
            </div>
          </form>
        )}
      </div>
    </Carte>
  )
}
